import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MagicWand, FileXls, FilePdf, PencilSimple, Trash, ChatCircle, Sparkle, ArrowRight, UploadSimple, ArrowSquareOut, X } from "@phosphor-icons/react";
import { RiskBadge, StatusPill } from "./RiskBadge";
import AttachmentsSection from "./AttachmentsSection";
import BulkImportDialog from "./BulkImportDialog";
import { useNavigate } from "react-router-dom";
import ObservationsToolbar from "./ObservationsToolbar";
import AchievementToast from "./AchievementToast";

const RISKS = ["High", "Medium", "Low"];
const STATUSES = ["Open", "In-Progress", "Resolved", "Closed"];

export default function ObservationsTable({ companyId, cycleId, cycles, role, onChange, onExport }) {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    area: "",
    observation: "",
    impact: "",
    recommendation: "",
    risk: "",
    use_ai: true,
  });
  const [aiBusy, setAiBusy] = useState(false);

  const [resModalObs, setResModalObs] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Bulk-selection state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ status: "", risk: "", area: "" });
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      const r = await api.get(`/observations?cycle_id=${cycleId}`);
      setObservations(r.data);
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Clear selection whenever the underlying cycle changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [cycleId]);

  useEffect(() => {
    const h = () => reload();
    window.addEventListener("observations-reload", h);
    return () => window.removeEventListener("observations-reload", h);
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setForm({ area: "", observation: "", impact: "", recommendation: "", risk: "", use_ai: true });
    setOpenForm(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      area: o.area,
      observation: o.observation,
      impact: o.impact || "",
      recommendation: o.recommendation || "",
      risk: o.risk,
      use_ai: false,
    });
    setOpenForm(true);
  };

  const runAISuggest = async () => {
    if (!form.area || !form.observation) {
      toast.error("Area and Observation are required for AI assist");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/observations/ai-suggest", {
        area: form.area,
        observation: form.observation,
      });
      setForm((f) => ({
        ...f,
        impact: r.data.impact || f.impact,
        recommendation: r.data.recommendation || f.recommendation,
        risk: r.data.risk || f.risk,
      }));
      toast.success(`AI suggested: ${r.data.risk} risk · weight ${r.data.risk_weight}`);
    } catch (err) {
      toast.error("AI assist failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/observations/${editing.id}`, {
          area: form.area,
          observation: form.observation,
          impact: form.impact,
          recommendation: form.recommendation,
          risk: form.risk || undefined,
        });
        toast.success("Observation updated");
      } else {
        await api.post("/observations", {
          company_id: companyId,
          cycle_id: cycleId,
          area: form.area,
          observation: form.observation,
          impact: form.impact,
          recommendation: form.recommendation,
          risk: form.risk || undefined,
          use_ai: form.use_ai,
        });
        toast.success("Observation added");
      }
      setOpenForm(false);
      reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteObs = async (id) => {
    if (!window.confirm("Delete this observation?")) return;
    await api.delete(`/observations/${id}`);
    toast.success("Deleted");
    reload();
    onChange?.();
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (visibleIds, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) visibleIds.forEach((id) => next.add(id));
      else visibleIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} observation(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/observations/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} observation(s)`);
      clearSelection();
      reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const openBulkEdit = () => {
    setBulkEditForm({ status: "", risk: "", area: "" });
    setBulkEditOpen(true);
  };

  const submitBulkEdit = async (e) => {
    e.preventDefault();
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const payload = { ids };
    if (bulkEditForm.status) payload.status = bulkEditForm.status;
    if (bulkEditForm.risk) payload.risk = bulkEditForm.risk;
    if (bulkEditForm.area.trim()) payload.area = bulkEditForm.area.trim();
    if (!payload.status && !payload.risk && !payload.area) {
      toast.error("Choose at least one field to update");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/observations/bulk-update", payload);
      toast.success(`Updated ${r.data.modified} observation(s)`);
      setBulkEditOpen(false);
      clearSelection();
      reload();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const changeStatus = async (id, status) => {
    await api.put(`/observations/${id}`, { status });
    reload();
    onChange?.();
  };

  const openResolutions = async (o) => {
    setResModalObs(o);
    const r = await api.get(`/observations/${o.id}/resolutions`);
    setResolutions(r.data);
  };

  const reviewResolution = async (rid, action) => {
    const comment = window.prompt(`${action} — add comment (optional):`, "") || "";
    await api.put(`/resolutions/${rid}/review`, {
      review_status: action,
      auditor_comment: comment,
    });
    toast.success(`Resolution ${action}`);
    const r = await api.get(`/observations/${resModalObs.id}/resolutions`);
    setResolutions(r.data);
    reload();
    onChange?.();
  };

  const cycleName = cycles?.find((c) => c.id === cycleId)?.name;

  return (
    <div data-testid="observations-table-wrapper">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold" style={{ fontFamily: "Geist" }}>
            Observations — {cycleName || "—"}
          </h3>
          <p className="text-xs text-slate-500">{observations.length} total · sortable by Sr. No.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {role === "audit_team" && (
            <>
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn">
                <UploadSimple size={14} className="mr-1" /> Bulk Import
              </Button>
              <Button variant="outline" onClick={() => onExport("excel")} data-testid="export-excel-btn">
                <FileXls size={14} className="mr-1" /> Excel
              </Button>
              <Button variant="outline" onClick={() => onExport("pdf")} data-testid="export-pdf-btn">
                <FilePdf size={14} className="mr-1" /> PDF
              </Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={openNew} data-testid="add-observation-btn">
                <Plus size={14} className="mr-1" /> New Observation
              </Button>
            </>
          )}
        </div>
      </div>

      <ObservationsToolbar
        observations={observations}
        role={role}
        onStatusChange={() => { reload(); onChange?.(); }}
      >
        {(filtered) => {
          const visibleIds = filtered.map((o) => o.id);
          const visibleSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
          const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
          const someVisibleSelected = visibleSelectedCount > 0 && visibleSelectedCount < visibleIds.length;
          const canBulk = role === "audit_team";
          return (
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        {canBulk && selectedIds.size > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-900 text-white text-sm"
            data-testid="bulk-actions-bar"
          >
            <span className="font-medium" data-testid="bulk-selected-count">
              {selectedIds.size} selected
            </span>
            <span className="text-slate-400 text-xs">·</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={openBulkEdit}
              disabled={bulkBusy}
              data-testid="bulk-edit-btn"
            >
              <PencilSimple size={14} className="mr-1" /> Bulk Edit
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="bulk-delete-btn"
            >
              <Trash size={14} className="mr-1" /> Delete
            </Button>
            <button
              onClick={clearSelection}
              className="ml-auto p-1 hover:bg-slate-800 rounded"
              title="Clear selection"
              data-testid="bulk-clear-btn"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {canBulk && (
                  <th className="px-3 py-2 text-left w-8">
                    <Checkbox
                      checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleAllVisible(visibleIds, !!v)}
                      aria-label="Select all"
                      data-testid="bulk-select-all"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left">Sr.</th>
                <th className="px-3 py-2 text-left">Risk</th>
                <th className="px-3 py-2 text-left">Area</th>
                <th className="px-3 py-2 text-left">Observation</th>
                <th className="px-3 py-2 text-left">Impact</th>
                <th className="px-3 py-2 text-left">Recommendation</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canBulk ? 9 : 8} className="py-8 text-center text-sm text-slate-500">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={canBulk ? 9 : 8} className="py-12 text-center text-sm text-slate-500" data-testid="observations-empty">No observations match the current filters.</td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className={`border-t border-slate-100 hover:bg-slate-50/60 align-top cursor-pointer ${selectedIds.has(o.id) ? "bg-blue-50/40" : ""}`} data-testid={`obs-row-${o.id}`} onClick={(e) => {
                  // Don't navigate when clicking on interactive cells
                  if (e.target.closest("button, [role='combobox'], [role='checkbox'], [data-testid^='status-select-'], [data-testid^='row-select-'], a, label")) return;
                  navigate(`${role === "audit_team" ? "/audit" : "/client"}/observations/${o.id}`);
                }}>
                  {canBulk && (
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selectedIds.has(o.id)}
                        onCheckedChange={() => toggleRow(o.id)}
                        aria-label={`Select observation ${o.sr_no}`}
                        data-testid={`row-select-${o.id}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-3 text-slate-600 font-medium tabular-nums">
                    {o.sr_no}
                    {o.rolled_forward_from && (
                      <div className="text-[9px] uppercase tracking-wider text-blue-600 mt-0.5">Rolled fwd</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <RiskBadge risk={o.risk} />
                    <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Sparkle size={9} /> wt {o.ai_risk_weight}
                    </div>
                  </td>
                  <td className="px-3 py-3 font-medium text-slate-800">{o.area}</td>
                  <td className="px-3 py-3 max-w-[280px] text-slate-700 whitespace-pre-line">{o.observation}</td>
                  <td className="px-3 py-3 max-w-[220px] text-slate-600 whitespace-pre-line">{o.impact}</td>
                  <td className="px-3 py-3 max-w-[220px] text-slate-600 whitespace-pre-line">{o.recommendation}</td>
                  <td className="px-3 py-3">
                    {role === "audit_team" ? (
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-[130px]" data-testid={`status-select-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <StatusPill status={o.status} />
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => navigate(`${role === "audit_team" ? "/audit" : "/client"}/observations/${o.id}`)}
                        className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"
                        title="Open detail"
                        data-testid={`open-detail-${o.id}`}
                      >
                        <ArrowSquareOut size={15} />
                      </button>
                      {role === "audit_team" && (
                        <>
                          <button onClick={() => openEdit(o)} className="p-1.5 hover:bg-slate-100 text-slate-600 rounded" title="Edit" data-testid={`edit-obs-${o.id}`}>
                            <PencilSimple size={15} />
                          </button>
                          <button onClick={() => deleteObs(o.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" title="Delete" data-testid={`delete-obs-${o.id}`}>
                            <Trash size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
          );
        }}
      </ObservationsToolbar>

      <AchievementToast observations={observations} cycleId={cycleId} />

      {/* New/Edit Dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl" data-testid="observation-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit observation" : "New observation"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Area / Department</Label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} required placeholder="Accounting" data-testid="obs-area-input" />
              </div>
              <div>
                <Label>Risk (optional — AI will assign if blank)</Label>
                <Select value={form.risk || "AUTO"} onValueChange={(v) => setForm({ ...form, risk: v === "AUTO" ? "" : v })}>
                  <SelectTrigger data-testid="obs-risk-select"><SelectValue placeholder="Auto by AI" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AUTO">Auto (AI)</SelectItem>
                    {RISKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observation</Label>
              <Textarea rows={3} value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} required data-testid="obs-text-input" />
            </div>
            <div className="flex items-center justify-end">
              <Button type="button" variant="outline" size="sm" onClick={runAISuggest} disabled={aiBusy} data-testid="ai-suggest-btn">
                <MagicWand size={14} className="mr-1" />
                {aiBusy ? "Analyzing..." : "AI Suggest Impact + Recommendation + Risk"}
              </Button>
            </div>
            <div>
              <Label>Impact</Label>
              <Textarea rows={2} value={form.impact} onChange={(e) => setForm({ ...form, impact: e.target.value })} data-testid="obs-impact-input" />
            </div>
            <div>
              <Label>Recommendation</Label>
              <Textarea rows={2} value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} data-testid="obs-reco-input" />
            </div>
            {editing && (
              <AttachmentsSection observationId={editing.id} role={role} compact />
            )}
            <DialogFooter>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="obs-save-btn">
                {editing ? "Save changes" : "Add observation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Resolutions Dialog */}
      <Dialog open={!!resModalObs} onOpenChange={(v) => !v && setResModalObs(null)}>
        <DialogContent className="max-w-2xl" data-testid="resolutions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChatCircle size={18} /> Resolutions
            </DialogTitle>
          </DialogHeader>
          {resModalObs && (
            <div className="text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Observation #{resModalObs.sr_no}</div>
                <div className="font-medium text-slate-800 mt-1">{resModalObs.observation}</div>
              </div>

              <div className="mb-3">
                <AttachmentsSection observationId={resModalObs.id} role={role} />
              </div>
              {resolutions.length === 0 && (
                <div className="text-sm text-slate-500 py-4 text-center" data-testid="no-resolutions">
                  Client hasn’t submitted a resolution yet.
                </div>
              )}
              <div className="space-y-3">
                {resolutions.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`resolution-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Submitted {new Date(r.submitted_at).toLocaleString()}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${
                        r.review_status === "Accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        r.review_status === "Rejected" ? "bg-red-50 text-red-700 border-red-200" :
                        r.review_status === "Needs More Info" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-slate-50 text-slate-600 border-slate-200"
                      }`}>{r.review_status}</span>
                    </div>
                    <div className="mt-2"><span className="text-slate-500 text-xs">Response: </span>{r.client_response}</div>
                    <div className="mt-1"><span className="text-slate-500 text-xs">Action taken: </span>{r.action_taken}</div>
                    {r.auditor_comment && (
                      <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                        Auditor: {r.auditor_comment}
                      </div>
                    )}
                    {role === "audit_team" && r.review_status === "Pending Review" && (
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => reviewResolution(r.id, "Accepted")} data-testid={`accept-${r.id}`}>Accept & Resolve</Button>
                        <Button size="sm" variant="outline" onClick={() => reviewResolution(r.id, "Needs More Info")} data-testid={`needsinfo-${r.id}`}>Needs more info</Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => reviewResolution(r.id, "Rejected")} data-testid={`reject-${r.id}`}>Reject</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        companyId={companyId}
        cycleId={cycleId}
        onImported={() => { reload(); onChange?.(); }}
      />

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Bulk edit {selectedIds.size} observation(s)</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBulkEdit} className="space-y-3">
            <p className="text-xs text-slate-500">
              Leave a field blank to keep it unchanged. Only filled fields will be applied to selected rows.
            </p>
            <div>
              <Label>Status</Label>
              <Select
                value={bulkEditForm.status || "KEEP"}
                onValueChange={(v) => setBulkEditForm({ ...bulkEditForm, status: v === "KEEP" ? "" : v })}
              >
                <SelectTrigger data-testid="bulk-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="KEEP">— No change —</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Risk</Label>
              <Select
                value={bulkEditForm.risk || "KEEP"}
                onValueChange={(v) => setBulkEditForm({ ...bulkEditForm, risk: v === "KEEP" ? "" : v })}
              >
                <SelectTrigger data-testid="bulk-edit-risk"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="KEEP">— No change —</SelectItem>
                  {RISKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Area / Department</Label>
              <Input
                placeholder="Leave blank to keep unchanged"
                value={bulkEditForm.area}
                onChange={(e) => setBulkEditForm({ ...bulkEditForm, area: e.target.value })}
                data-testid="bulk-edit-area"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBulkEditOpen(false)}
                data-testid="bulk-edit-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={bulkBusy}
                data-testid="bulk-edit-save"
              >
                {bulkBusy ? "Applying..." : "Apply to selected"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
